import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Sticky banner shown above unverified user dashboards.
 * Hits POST /api/auth/resend-verification to dispatch a fresh email.
 */
const EmailVerificationBanner = ({ email }) => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleResend = async () => {
    if (!email) {
      toast.error('Missing email — please sign in again.');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      setSent(true);
      toast.success('Verification email sent. Check your inbox.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not resend verification email.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="bg-amber-50 border-b border-amber-200"
      role="alert"
      data-testid="email-verification-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-start gap-3 flex-1">
          <Mail className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-amber-900">
              Please verify your email address
            </p>
            <p className="text-amber-800">
              We sent a verification link to <span className="font-medium">{email}</span>.
              Some features remain restricted until your email is confirmed.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="border-amber-400 text-amber-900 hover:bg-amber-100 whitespace-nowrap"
          onClick={handleResend}
          disabled={sending || sent}
          data-testid="resend-verification-btn"
        >
          {sending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          {sent && <CheckCircle2 className="h-4 w-4 mr-2" />}
          {sent ? 'Email sent' : sending ? 'Sending…' : 'Resend email'}
        </Button>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
